/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { isStoreInfoLocation, IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    deriveCrossEntityStockLabel,
    getCrossEntityInventoryQuantity,
    IFullOrgUnitAvailability,
    InventoryLevels,
    isCrossEntityStockLabelVisible,
    selectItemAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';

export interface ICrossEntityStoreFilterState {
    selectedRegion?: string;
    selectedLegalEntity?: string;
    inStockOnly?: boolean;
}

export interface ICrossEntityInventoryResources {
    inStockText: string;
    lowStockText: string;
    outOfStockText: string;
    inventoryAvailableText: string;
}

export interface ICrossEntityInventoryConfig {
    enableStockCheck?: boolean;
    outOfStockThreshold?: number;
    lowStockThreshold?: number;
    inventoryLevel?: InventoryLevels;
    inventoryRanges?: string;
}

export interface ICrossEntityInventoryLabels {
    availabilityLabel?: string;
    statusLabel?: string;
}

export enum StoreSelectorDataSource {
    legacyLocations = 'legacyLocations',
    crossEntityLocations = 'crossEntityLocations',
    legacyInventory = 'legacyInventory',
    crossEntityInventory = 'crossEntityInventory'
}

/**
 * Determines whether the current request represents a show-all-stores search.
 * @param radius - Effective search radius sent to the location API.
 * @returns Whether the result count should omit radius and distance units.
 */
export const isShowAllStoresSearch = (radius: number): boolean => radius === 0;

/**
 * Determines whether a location matches a preferred store. New Cross-LE preferences use legal-entity identity,
 * while preferences persisted before Cross-LE continue to match by store ID.
 * @param location - Store location to compare.
 * @param preferredStore - Persisted preferred-store information.
 * @returns Whether the location represents the preferred store.
 */
export const isPreferredStoreLocation = (location: OrgUnitLocation | undefined, preferredStore: IStoreInfo | undefined): boolean => {
    return isStoreInfoLocation(location, preferredStore);
};

/**
 * Selects the Store Selector data path without changing legacy behavior when Cross-LE is disabled.
 * @param crossEntityEnabled - Whether the Cross-LE eCommerce feature is enabled.
 * @param locatorMode - Whether the Store Selector is in location-only mode.
 * @returns The data source to invoke.
 */
export const getStoreSelectorDataSource = (
    crossEntityEnabled: boolean,
    locatorMode: boolean,
    enableStockCheck: boolean = true
): StoreSelectorDataSource => {
    if (locatorMode) {
        return crossEntityEnabled ? StoreSelectorDataSource.crossEntityLocations : StoreSelectorDataSource.legacyLocations;
    }

    if (crossEntityEnabled && !enableStockCheck) {
        return StoreSelectorDataSource.crossEntityLocations;
    }

    return crossEntityEnabled ? StoreSelectorDataSource.crossEntityInventory : StoreSelectorDataSource.legacyInventory;
};

/**
 * Determines whether a Cross-LE store is in stock for the selected inventory level and thresholds.
 * @param store - Product-scoped store availability.
 * @param config - Site inventory configuration.
 * @param productId - Product whose availability should be selected.
 * @returns Whether the store is in stock.
 */
export const isCrossEntityStoreInStock = (
    store: IFullOrgUnitAvailability | undefined,
    config: ICrossEntityInventoryConfig,
    productId?: number
): boolean => {
    const itemAvailability = selectItemAvailability(store?.OrgUnitAvailability?.ItemAvailabilities, productId);
    const node = {
        availableQuantity: itemAvailability?.AvailableQuantity,
        orderedSum: itemAvailability?.OrderedSum
    };
    const stockLabel = deriveCrossEntityStockLabel(node, config);
    return stockLabel ? stockLabel.status !== 'outOfStock' : getCrossEntityInventoryQuantity(node, config.inventoryLevel) > 0;
};

/**
 * Filters Cross-LE stores using Country as Region, DataAreaId as legal entity, and configured inventory availability.
 * @param stores - Unfiltered Cross-LE stores.
 * @param filterState - Active filter values.
 * @param productMode - Whether product inventory filtering applies.
 * @returns Filtered stores.
 */
export const filterCrossEntityStoreLocations = (
    stores: IFullOrgUnitAvailability[],
    filterState: ICrossEntityStoreFilterState,
    productMode: boolean,
    productId?: number,
    config: ICrossEntityInventoryConfig = {}
): IFullOrgUnitAvailability[] => {
    const inStockOnly = filterState.inStockOnly ?? true;

    return stores.filter(store => {
        const location = store.OrgUnitAvailability?.OrgUnitLocation;

        return (
            (!filterState.selectedRegion || location?.Country === filterState.selectedRegion) &&
            (!filterState.selectedLegalEntity || location?.DataAreaId === filterState.selectedLegalEntity) &&
            (!productMode || !inStockOnly || isCrossEntityStoreInStock(store, config, productId))
        );
    });
};

/**
 * Gets unique, non-empty filter values from Cross-LE store locations.
 * @param stores - Cross-LE stores.
 * @param field - Location field backing the filter.
 * @returns Unique filter values in source order.
 */
export const getCrossEntityFilterValues = (stores: IFullOrgUnitAvailability[], field: 'Country' | 'DataAreaId'): string[] => {
    const values = stores
        .map(store => {
            const location = store.OrgUnitAvailability?.OrgUnitLocation;
            return field === 'Country' ? location?.Country : location?.DataAreaId;
        })
        .filter((value): value is string => !!value);

    return values.filter((value, index) => values.indexOf(value) === index);
};

/**
 * Builds separate on-hand availability and inventory-status labels.
 * @param store - Product-scoped store availability.
 * @param config - Site inventory thresholds.
 * @param resources - Localized Store Selector resources.
 * @returns Availability and status labels.
 */
export const getCrossEntityInventoryLabels = (
    store: IFullOrgUnitAvailability | undefined,
    config: ICrossEntityInventoryConfig,
    resources: ICrossEntityInventoryResources,
    productId?: number
): ICrossEntityInventoryLabels => {
    const itemAvailability = selectItemAvailability(store?.OrgUnitAvailability?.ItemAvailabilities, productId);
    const hasAvailability = itemAvailability !== undefined;
    const node = {
        availableQuantity: itemAvailability?.AvailableQuantity,
        orderedSum: itemAvailability?.OrderedSum
    };
    const quantity = hasAvailability ? getCrossEntityInventoryQuantity(node, config.inventoryLevel) : undefined;
    const availabilityLabel =
        quantity === undefined
            ? undefined
            : resources.inventoryAvailableText
                  .replace('{count}', quantity.toString())
                  .replace('{unit}', itemAvailability?.UnitOfMeasure ?? '')
                  .replace(/\s+/g, ' ')
                  .trim();

    const stockLabel = deriveCrossEntityStockLabel(node, config);
    if (!isCrossEntityStockLabelVisible(stockLabel, config.inventoryRanges)) {
        return { availabilityLabel };
    }

    switch (stockLabel?.status) {
        case 'outOfStock':
            return { availabilityLabel, statusLabel: resources.outOfStockText };
        case 'lowStock':
            return {
                availabilityLabel,
                statusLabel: resources.lowStockText.replace('{count}', (stockLabel.quantity ?? 0).toString())
            };
        case 'inStock':
            return { availabilityLabel, statusLabel: resources.inStockText };
        default:
            return { availabilityLabel };
    }
};
